/**
 * Analytics bus.
 *
 * Single entry point for the rest of the app:
 *   import { track, trackScreen, identify, reset, setSuperProperties } from '@/lib/analytics';
 *
 * Fans out events to all registered destinations (PostHog now, Meta later).
 * Buffers events until destinations are ready / consent is granted.
 */
import type {
  AnalyticsDestination,
  EventName,
  EventProps,
  SuperProperties,
} from './types';
import { posthogDestination } from './destinations/posthog';
import { metaDestination } from './destinations/meta';
import { detectPlatform } from './super-properties';

const destinations: AnalyticsDestination[] = [posthogDestination, metaDestination];

interface BufferedEvent {
  type: 'track' | 'screen';
  name: string;
  props: EventProps;
}

const buffer: BufferedEvent[] = [];
// Kept generous on purpose: events fired before consent is granted must survive
// until the user accepts the cookie banner (which triggers a re-init + drain).
const MAX_BUFFER = 300;

let identity: { id: string; traits?: EventProps } | null = null;
let superProps: SuperProperties = { app_platform: detectPlatform(), locale: typeof navigator !== 'undefined' ? navigator.language : undefined };

const safe = (dest: AnalyticsDestination, fn: () => void) => {
  try { fn(); } catch (err) {
    console.error(`[Analytics:${dest.name}] error`, err);
  }
};

const drain = () => {
  if (!destinations.some((d) => d.isReady())) return;
  while (buffer.length > 0) {
    const evt = buffer.shift()!;
    destinations.forEach((d) => {
      if (!d.isReady()) return;
      safe(d, () => {
        if (evt.type === 'track') d.track(evt.name, evt.props);
        else d.trackScreen(evt.name, evt.props);
      });
    });
  }
};

export async function initAnalytics(): Promise<void> {
  // Apply current super properties to all destinations even before init —
  // each destination keeps its own copy that gets merged on every event.
  destinations.forEach((d) => safe(d, () => d.setSuperProperties(superProps)));
  await Promise.all(destinations.map((d) => Promise.resolve().then(() => d.init())));
  if (identity) {
    destinations.forEach((d) => {
      if (d.isReady()) safe(d, () => d.identify(identity!.id, identity!.traits));
    });
  }
  drain();
}

// When the user accepts/changes cookie consent, destinations may become ready.
// Re-run init (idempotent) and flush anything buffered meanwhile.
if (typeof window !== 'undefined') {
  window.addEventListener('careos:cookie-prefs-changed', () => {
    void initAnalytics();
  });
}


export function track(event: EventName, props: EventProps = {}): void {
  if (!destinations.some((d) => d.isReady())) {
    if (buffer.length < MAX_BUFFER) buffer.push({ type: 'track', name: event, props });
    return;
  }
  destinations.forEach((d) => {
    if (d.isReady()) safe(d, () => d.track(event, props));
  });
}

export function trackScreen(name: string, props: EventProps = {}): void {
  if (!destinations.some((d) => d.isReady())) {
    if (buffer.length < MAX_BUFFER) buffer.push({ type: 'screen', name, props });
    return;
  }
  destinations.forEach((d) => {
    if (d.isReady()) safe(d, () => d.trackScreen(name, props));
  });
}

export function identify(distinctId: string, traits?: EventProps): void {
  identity = { id: distinctId, traits };
  destinations.forEach((d) => {
    if (d.isReady()) safe(d, () => d.identify(distinctId, traits));
  });
}

export function setPersonProperties(props: EventProps): void {
  destinations.forEach((d) => {
    if (d.isReady() && d.setPersonProperties) safe(d, () => d.setPersonProperties!(props));
  });
}

export function reset(): void {
  identity = null;
  destinations.forEach((d) => safe(d, () => d.reset()));
}

export function setSuperProperties(props: SuperProperties): void {
  superProps = { ...superProps, ...props };
  destinations.forEach((d) => safe(d, () => d.setSuperProperties(superProps)));
}

export function getSuperProperties(): SuperProperties {
  return { ...superProps };
}
